import React from "react";
import { Building2, Mail, Phone, Tag } from "lucide-react";
import AIScoreBadge from "./AIScoreBadge";

/**
 * Hero header — type Axonaut.
 * Avatar lettre, nom + entreprise, contact rapide, badges (statut + scoring IA), actions.
 *
 * Props :
 *   entity        : prospect ou opportunity
 *   kind          : "prospect" | "opportunity"
 *   statusBadge   : { label, color } (résolu côté parent à partir de status/stage)
 *   aiEvaluation  : { score, tier, headline, ... } (peut être undefined)
 *   actions       : ReactNode — boutons (Appeler, Email, Réévaluer, etc.)
 */
const initials = (entity) => {
  const fn = entity.first_name || entity.contact_name?.split(" ")[0] || "";
  const ln = entity.last_name || entity.contact_name?.split(" ").slice(1).join(" ") || entity.company || "";
  return ((fn[0] || "") + (ln[0] || "")).toUpperCase() || "?";
};

const fullName = (entity) =>
  [entity.first_name, entity.last_name].filter(Boolean).join(" ") ||
  entity.contact_name ||
  entity.client_name ||
  entity.title ||
  entity.company ||
  "Sans nom";

export const EntityHero = ({ entity, kind, statusBadge, aiEvaluation, actions, testId = "entity-hero" }) => {
  if (!entity) return null;
  const phone = entity.phone || entity.contact_phone || entity.phone_mobile || "";
  const email = entity.email || entity.contact_email || "";
  const company = entity.company || (kind === "opportunity" ? entity.client_name : "") || "";
  const tagList = entity.tags || [];

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-[#1a1525] via-[#14101c] to-[#0e0a17] dark:from-[#1a1525] dark:via-[#14101c] dark:to-[#0e0a17] border border-white/10 p-5 md:p-6"
      data-testid={testId}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-5">
        {/* Avatar lettre — gradient personnalisé */}
        <div className="flex-shrink-0">
          <div
            className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-[#f7c935] via-[#f5b800] to-[#e6a800] flex items-center justify-center text-2xl md:text-3xl font-black text-[#0e0a17] shadow-lg shadow-[#f7c935]/20"
            data-testid="entity-hero-avatar"
          >
            {initials(entity)}
          </div>
        </div>

        {/* Identité + contact */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1
                className="text-xl md:text-2xl font-bold text-white truncate"
                data-testid="entity-hero-name"
              >
                {fullName(entity)}
              </h1>
              {company && (
                <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-1">
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="truncate">{company}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {statusBadge && (
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusBadge.bg || ""}`}
                  style={statusBadge.bg ? {} : { backgroundColor: `${statusBadge.color}22`, color: statusBadge.color }}
                  data-testid="entity-hero-status"
                >
                  {statusBadge.label}
                </span>
              )}
              {aiEvaluation && typeof aiEvaluation.score === "number" && (
                <AIScoreBadge
                  tier={aiEvaluation.tier}
                  score={aiEvaluation.score}
                  size="md"
                  testId="entity-hero-ai-score"
                />
              )}
            </div>
          </div>

          {/* AI headline */}
          {aiEvaluation?.headline && (
            <div
              className="mt-3 text-sm text-slate-300 italic border-l-2 border-[#f7c935] pl-3"
              data-testid="entity-hero-ai-headline"
            >
              « {aiEvaluation.headline} »
            </div>
          )}

          {/* Contact pills */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {email && (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 transition-colors"
                data-testid="entity-hero-email"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="truncate max-w-[200px]">{email}</span>
              </a>
            )}
            {phone && (
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
                data-testid="entity-hero-phone"
              >
                <Phone className="w-3.5 h-3.5" />
                {phone}
              </a>
            )}
            {tagList.slice(0, 4).map((t, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300"
              >
                <Tag className="w-3 h-3" />
                {t}
              </span>
            ))}
            {tagList.length > 4 && (
              <span className="text-xs text-slate-500">+{tagList.length - 4}</span>
            )}
          </div>

          {/* Actions */}
          {actions && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-white/5" data-testid="entity-hero-actions">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntityHero;
